'use client';

import { createContext, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { interactionResponse } from '@zillow/yield-callback';
import type { ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { Toast, useToast } from '@zillow/constellation-10';
import { useTranslations } from 'next-intl';
import { HpModifiableUserInteraction } from '@zg-rentals/ts-hotpads-api-client';
import type { ExtendedListing } from '@/src/lib/api/endpoints/listing';
import { tryNativeShare } from '@/src/lib/utils/shareUtils';
import { analytics } from '@/src/components/features/property/lib';
import { emitTrackHdpNativeShareEvent } from '@/src/analytics/hotpads/events';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import type {
  ContactClickstreamContext,
  ContactSubmissionContext,
} from '@/src/components/features/property/ContactForm/types';
import { useAddUserInteraction, useRemoveUserInteraction } from '@/src/lib/hooks/query/useUserInteractions';
import { useUserListings } from '@/src/lib/hooks/query/useUserListings';
import { useCurrentUser } from '@/src/lib/hooks/query/useUserQuery';
import {
  DEFAULT_LISTING_ACTION_ANALYTICS,
  getListingActionAlias,
  getListingActionClickstreamListing,
  type ListingActionAnalyticsContext,
  listingHasFavoriteUserItem,
  listingHasHiddenUserItem,
  type ListingLike,
  SHARE_CLICKSTREAM_CONTEXTS,
  type ShareClickstreamContext,
} from './listingActionUtils';
import { ListingActionSignInModal } from './ListingActionSignInModal';

const ContactFormOverlay = dynamic(() =>
  import('@/src/components/features/property/ContactForm/ContactFormOverlay').then((mod) => mod.ContactFormOverlay),
);

const PostContactFlowModal = dynamic(() =>
  import('@/src/components/features/property/ContactForm/PostContactFlowModal').then((mod) => mod.PostContactFlowModal),
);

const ShareFormOverlay = dynamic(() =>
  import('@/src/components/features/property/ShareForm/ShareFormOverlay').then((mod) => mod.ShareFormOverlay),
);

const ReportListingOverlay = dynamic(() =>
  import('@/src/components/features/property/ReportListingOverlay/ReportListingOverlay').then(
    (mod) => mod.ReportListingOverlay,
  ),
);

type ListingActionListing = PropertyDetailListing | ExtendedListing | ListingLike;

type ListingActionOpenOptions = Partial<ListingActionAnalyticsContext> & {
  title?: string;
  /** Pre-fills the contact form's message box. */
  initialMessage?: string;
};

type ListingActionContextValue = {
  openContact: (listing: ListingActionListing | null | undefined, options?: ListingActionOpenOptions) => Promise<void>;
  openShare: (listing: ListingActionListing | null | undefined, context?: ShareClickstreamContext) => void;
  openReport: (
    listing: ListingActionListing | null | undefined,
    analyticsContext?: ListingActionAnalyticsContext,
  ) => void;
  isListingSaved: (listing: ListingLike | null | undefined) => boolean;
  isListingSavePending: (listing: ListingLike | null | undefined) => boolean;
  isListingHidden: (listing: ListingLike | null | undefined) => boolean;
  reconcileFavoriteOverride: (listing: ListingLike | null | undefined) => void;
  toggleSaveListing: (
    listing: ListingLike | null | undefined,
    analyticsContext?: ListingActionAnalyticsContext,
  ) => Promise<boolean>;
  toggleHideListing: (
    listing: ListingLike | null | undefined,
    analyticsContext?: ListingActionAnalyticsContext,
  ) => void;
};

type ActiveContact = {
  listing: PropertyDetailListing | ExtendedListing | null | undefined;
  title: string;
  clickstreamContext: ContactClickstreamContext;
  initialMessage?: string;
};

type ActiveShare = {
  listing: PropertyDetailListing | ExtendedListing;
  triggerLocation: ShareClickstreamContext['triggerLocation'];
};

type ActiveReport = {
  listing: PropertyDetailListing | ExtendedListing;
};

type PendingHide = {
  alias: string;
  analyticsContext: ListingActionAnalyticsContext;
};

type PendingFavorite = {
  alias: string;
  analyticsContext: ListingActionAnalyticsContext;
  requestedAt: number;
};

export const ListingActionContext = createContext<ListingActionContextValue | null>(null);
const PENDING_FAVORITE_STORAGE_KEY = 'hotpads-pending-favorite';
const PENDING_FAVORITE_MAX_AGE_MS = 30 * 60 * 1000;
const PENDING_FAVORITE_REDIRECT_PARAM = 'favorite';
const MAX_PENDING_FAVORITE_REDIRECT_PARAMS = 5;
const FAVORITE_OVERRIDES_STORAGE_KEY = 'hotpads-favorite-overrides';
const FAVORITE_OVERRIDE_EVENT_STORAGE_KEY = 'hotpads-favorite-override-event';
const FAVORITE_OVERRIDES_MAX_AGE_MS = 30 * 60 * 1000;

type StoredFavoriteOverride = {
  saved: boolean;
  updatedAt: number;
};

type StoredFavoriteOverrideEvent = StoredFavoriteOverride & {
  alias: string;
};

type FavoriteIntentController = {
  confirmedSaved: boolean;
  desiredSaved: boolean;
  interaction: ReturnType<typeof interactionResponse> | undefined;
  revision: number;
  running: boolean;
};

function getCurrentRelativeUrl(): string {
  if (typeof window === 'undefined') return '/';
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function mergePendingFavorites(...favoriteGroups: Array<Array<PendingFavorite>>): Array<PendingFavorite> {
  const pendingFavoritesByAlias = new Map<string, PendingFavorite>();
  favoriteGroups.flat().forEach((pendingFavorite) => {
    pendingFavoritesByAlias.set(pendingFavorite.alias, pendingFavorite);
  });
  return Array.from(pendingFavoritesByAlias.values());
}

function withPendingFavoriteRedirectParams(path: string, aliases: Array<string>): string {
  const url = new URL(path, 'https://hotpads.com');
  url.searchParams.delete(PENDING_FAVORITE_REDIRECT_PARAM);
  aliases
    .slice(0, MAX_PENDING_FAVORITE_REDIRECT_PARAMS)
    .forEach((alias) => url.searchParams.append(PENDING_FAVORITE_REDIRECT_PARAM, alias));
  return `${url.pathname}${url.search}${url.hash}`;
}

function readPendingFavorites(): Array<PendingFavorite> {
  if (typeof window === 'undefined') return [];

  try {
    const stored = window.sessionStorage.getItem(PENDING_FAVORITE_STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored) as PendingFavorite | Array<PendingFavorite>;
    const storedPendingFavorites = Array.isArray(parsed) ? parsed : [parsed];
    const freshPendingFavorites = mergePendingFavorites(
      storedPendingFavorites.filter(
        (pendingFavorite) =>
          Boolean(pendingFavorite?.alias) && Date.now() - pendingFavorite.requestedAt <= PENDING_FAVORITE_MAX_AGE_MS,
      ),
    );
    if (freshPendingFavorites.length === 0) {
      window.sessionStorage.removeItem(PENDING_FAVORITE_STORAGE_KEY);
      return [];
    }
    return freshPendingFavorites;
  } catch {
    window.sessionStorage.removeItem(PENDING_FAVORITE_STORAGE_KEY);
    return [];
  }
}

function readPendingFavoritesFromUrl(): Array<PendingFavorite> {
  if (typeof window === 'undefined') return [];

  const url = new URL(window.location.href);
  const aliases = Array.from(new Set(url.searchParams.getAll(PENDING_FAVORITE_REDIRECT_PARAM).filter(Boolean))).slice(
    0,
    MAX_PENDING_FAVORITE_REDIRECT_PARAMS,
  );
  if (aliases.length === 0) return [];

  url.searchParams.delete(PENDING_FAVORITE_REDIRECT_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);

  const requestedAt = Date.now();
  return aliases.map((alias) => ({
    alias,
    analyticsContext: DEFAULT_LISTING_ACTION_ANALYTICS,
    requestedAt,
  }));
}

function writePendingFavorites(pendingFavorites: Array<PendingFavorite>): void {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(PENDING_FAVORITE_STORAGE_KEY, JSON.stringify(pendingFavorites));
  } catch {
    window.sessionStorage.removeItem(PENDING_FAVORITE_STORAGE_KEY);
  }
}

function clearPendingFavorites(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(PENDING_FAVORITE_STORAGE_KEY);
}

function readFavoriteOverrides(): Record<string, StoredFavoriteOverride> {
  if (typeof window === 'undefined') return {};

  try {
    const stored = window.sessionStorage.getItem(FAVORITE_OVERRIDES_STORAGE_KEY);
    if (!stored) return {};

    const parsed = JSON.parse(stored) as Record<string, StoredFavoriteOverride>;
    const now = Date.now();
    const freshEntries = Object.entries(parsed).filter(
      ([, override]) =>
        typeof override?.saved === 'boolean' &&
        typeof override.updatedAt === 'number' &&
        now - override.updatedAt <= FAVORITE_OVERRIDES_MAX_AGE_MS,
    );

    if (freshEntries.length !== Object.keys(parsed).length) {
      if (freshEntries.length === 0) {
        window.sessionStorage.removeItem(FAVORITE_OVERRIDES_STORAGE_KEY);
      } else {
        window.sessionStorage.setItem(FAVORITE_OVERRIDES_STORAGE_KEY, JSON.stringify(Object.fromEntries(freshEntries)));
      }
    }

    return Object.fromEntries(freshEntries);
  } catch {
    window.sessionStorage.removeItem(FAVORITE_OVERRIDES_STORAGE_KEY);
    return {};
  }
}

function writeFavoriteOverrides(overrides: Record<string, StoredFavoriteOverride>): void {
  if (typeof window === 'undefined') return;

  try {
    const entries = Object.entries(overrides);
    if (entries.length === 0) {
      window.sessionStorage.removeItem(FAVORITE_OVERRIDES_STORAGE_KEY);
      return;
    }

    window.sessionStorage.setItem(FAVORITE_OVERRIDES_STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    window.sessionStorage.removeItem(FAVORITE_OVERRIDES_STORAGE_KEY);
  }
}

function writeFavoriteOverrideEvent(override: StoredFavoriteOverrideEvent): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(FAVORITE_OVERRIDE_EVENT_STORAGE_KEY, JSON.stringify(override));
  } catch {
    window.localStorage.removeItem(FAVORITE_OVERRIDE_EVENT_STORAGE_KEY);
  }
}

function readFavoriteOverrideEvent(value: string | null): StoredFavoriteOverrideEvent | null {
  if (!value) return null;

  try {
    const parsed = JSON.parse(value) as StoredFavoriteOverrideEvent;
    if (!parsed.alias || typeof parsed.saved !== 'boolean') return null;
    if (typeof parsed.updatedAt !== 'number') return null;
    if (Date.now() - parsed.updatedAt > FAVORITE_OVERRIDES_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function readLatestFavoriteOverrideEvent(): StoredFavoriteOverrideEvent | null {
  if (typeof window === 'undefined') return null;

  try {
    return readFavoriteOverrideEvent(window.localStorage.getItem(FAVORITE_OVERRIDE_EVENT_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function ListingActionProvider({ children }: { children: ReactNode }) {
  const tAuth = useTranslations('auth');
  const tHdp = useTranslations('hdp');
  const { data: currentUser } = useCurrentUser();
  const { data: favoriteListingsData, isFetching: favoriteListingsFetching } = useUserListings('favorite', {
    enabled: currentUser?.loggedIn === true,
  });
  const { data: hiddenListingsData } = useUserListings('hidden', {
    enabled: currentUser?.loggedIn === true,
  });
  const favoritedAliases = useMemo(
    () => new Set((favoriteListingsData?.listings ?? []).map((l) => l.aliasEncoded).filter((a): a is string => !!a)),
    [favoriteListingsData],
  );
  const hiddenAliases = useMemo(
    () => new Set((hiddenListingsData?.listings ?? []).map((l) => l.aliasEncoded).filter((a): a is string => !!a)),
    [hiddenListingsData],
  );
  const { mutateAsync: addUserInteraction } = useAddUserInteraction();
  const { mutateAsync: removeUserInteraction } = useRemoveUserInteraction();
  const { enqueueToast } = useToast();
  const [activeContact, setActiveContact] = useState<ActiveContact | null>(null);
  const [activeShare, setActiveShare] = useState<ActiveShare | null>(null);
  const [activeReport, setActiveReport] = useState<ActiveReport | null>(null);
  const [postContactContext, setPostContactContext] = useState<ContactSubmissionContext | null>(null);
  const [favoriteOverrides, setFavoriteOverrides] = useState<Record<string, StoredFavoriteOverride>>({});
  const [pendingFavorites, setPendingFavorites] = useState<Array<PendingFavorite>>([]);
  const favoriteControllersRef = useRef(new Map<string, FavoriteIntentController>());
  const favoriteRevisionsRef = useRef(new Map<string, number>());
  const resubmittedFavoriteRevisionsRef = useRef(new Map<string, number>());
  const favoritedAliasesRef = useRef(favoritedAliases);
  favoritedAliasesRef.current = favoritedAliases;
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authRedirectPath, setAuthRedirectPath] = useState('/');
  // Hide is login-gated like favorites, but uses lighter in-memory optimistic
  // state (no cross-tab / redirect persistence) — a re-click after a redirect
  // sign-in is acceptable for v1.
  const [hiddenOverrides, setHiddenOverrides] = useState<Record<string, boolean>>({});
  const [pendingHide, setPendingHide] = useState<PendingHide | null>(null);
  const completedHideRef = useRef<PendingHide | null>(null);
  const [hideAuthModalOpen, setHideAuthModalOpen] = useState(false);
  const [hideAuthRedirectPath, setHideAuthRedirectPath] = useState('/');

  useEffect(() => {
    const storedPendingFavorites = readPendingFavorites();
    const urlPendingFavorites = readPendingFavoritesFromUrl();
    const nextPendingFavorites = mergePendingFavorites(urlPendingFavorites, storedPendingFavorites);
    if (nextPendingFavorites.length > 0) {
      setPendingFavorites(nextPendingFavorites);
    }
    const storedOverrides = readFavoriteOverrides();
    Object.entries(storedOverrides).forEach(([alias, override]) => {
      favoriteRevisionsRef.current.set(
        alias,
        Math.max(favoriteRevisionsRef.current.get(alias) ?? 0, override.updatedAt),
      );
    });
    setFavoriteOverrides((current) => ({ ...storedOverrides, ...current }));
  }, []);

  // Sign-out in this tab: drop optimistic overrides so they can't leak to the next account.
  const loggedIn = currentUser?.loggedIn === true;
  const wasLoggedInRef = useRef(loggedIn);
  useEffect(() => {
    if (wasLoggedInRef.current && !loggedIn) {
      setFavoriteOverrides({});
      writeFavoriteOverrides({});
      favoriteControllersRef.current.clear();
      favoriteRevisionsRef.current.clear();
      resubmittedFavoriteRevisionsRef.current.clear();
      setHiddenOverrides({});
    }
    wasLoggedInRef.current = loggedIn;
  }, [loggedIn]);

  const applyFavoriteOverride = useCallback((alias: string, override: StoredFavoriteOverride, broadcast: boolean) => {
    const currentRevision = favoriteRevisionsRef.current.get(alias) ?? 0;
    if (override.updatedAt <= currentRevision) return false;

    favoriteRevisionsRef.current.set(alias, override.updatedAt);
    setFavoriteOverrides((current) => {
      if ((current[alias]?.updatedAt ?? 0) >= override.updatedAt) return current;
      const next = { ...current, [alias]: override };
      writeFavoriteOverrides(next);
      return next;
    });
    if (broadcast) {
      writeFavoriteOverrideEvent({ alias, ...override });
    }
    return true;
  }, []);

  const updateFavoriteOverride = useCallback(
    (alias: string, saved: boolean, broadcast = true) => {
      const previousEvent = readLatestFavoriteOverrideEvent();
      const updatedAt = Math.max(
        Date.now(),
        (favoriteRevisionsRef.current.get(alias) ?? 0) + 1,
        (previousEvent?.updatedAt ?? 0) + 1,
      );
      const override = { saved, updatedAt };
      applyFavoriteOverride(alias, override, broadcast);
      return override;
    },
    [applyFavoriteOverride],
  );

  const clearFavoriteOverride = useCallback((alias: string, revision: number) => {
    setFavoriteOverrides((current) => {
      if (current[alias]?.updatedAt !== revision) return current;
      const next = { ...current };
      delete next[alias];
      writeFavoriteOverrides(next);
      return next;
    });
  }, []);

  const reconcileFavoriteOverride = useCallback(
    (listing: ListingLike | null | undefined) => {
      if (!currentUser?.loggedIn || !listing?.userItemTypes) return;
      const alias = getListingActionAlias(listing);
      if (!alias) return;

      const override = favoriteOverrides[alias];
      const serverSaved = favoritedAliases.has(alias) || listingHasFavoriteUserItem(listing);
      if (override === undefined || override.saved !== serverSaved || favoriteControllersRef.current.has(alias)) {
        return;
      }

      clearFavoriteOverride(alias, override.updatedAt);
    },
    [clearFavoriteOverride, currentUser?.loggedIn, favoriteOverrides, favoritedAliases],
  );

  const showFavoriteError = useCallback(() => {
    enqueueToast(
      <Toast tone="critical" role="status">
        {tAuth('favoriteSaveError')}
      </Toast>,
    );
  }, [enqueueToast, tAuth]);

  const runFavoriteController = useCallback(
    async (alias: string, controller: FavoriteIntentController) => {
      controller.running = true;

      while (favoriteControllersRef.current.get(alias) === controller) {
        const revision = controller.revision;
        const desiredSaved = controller.desiredSaved;

        try {
          await controller.interaction;
        } catch {
          if (controller.revision === revision) {
            const rollback = updateFavoriteOverride(alias, controller.confirmedSaved);
            controller.desiredSaved = controller.confirmedSaved;
            controller.interaction = undefined;
            controller.revision = rollback.updatedAt;
          }
          if (controller.desiredSaved === controller.confirmedSaved) break;
          continue;
        }

        if (controller.revision !== revision) continue;

        if (controller.confirmedSaved !== desiredSaved) {
          try {
            const postData = { interaction: HpModifiableUserInteraction.FAVORITE, listingAlias: alias };
            if (desiredSaved) {
              await addUserInteraction({ postData });
            } else {
              await removeUserInteraction({ postData });
            }
            controller.confirmedSaved = desiredSaved;
          } catch {
            if (controller.revision === revision) {
              const rollback = updateFavoriteOverride(alias, controller.confirmedSaved);
              controller.desiredSaved = controller.confirmedSaved;
              controller.interaction = undefined;
              controller.revision = rollback.updatedAt;
              showFavoriteError();
            }
          }
        }

        if (controller.desiredSaved === controller.confirmedSaved) break;
      }

      if (favoriteControllersRef.current.get(alias) === controller) {
        favoriteControllersRef.current.delete(alias);
      }
    },
    [addUserInteraction, removeUserInteraction, showFavoriteError, updateFavoriteOverride],
  );

  const submitFavoriteIntent = useCallback(
    (
      alias: string,
      saved: boolean,
      confirmedSaved: boolean,
      revision: number,
      interaction: ReturnType<typeof interactionResponse> | undefined,
    ) => {
      const currentController = favoriteControllersRef.current.get(alias);
      if (currentController && revision <= currentController.revision) return;

      const controller =
        currentController ??
        ({
          confirmedSaved,
          desiredSaved: saved,
          interaction,
          revision,
          running: false,
        } satisfies FavoriteIntentController);
      controller.desiredSaved = saved;
      controller.interaction = interaction;
      controller.revision = revision;
      favoriteControllersRef.current.set(alias, controller);

      if (!controller.running) {
        void runFavoriteController(alias, controller);
      }
    },
    [runFavoriteController],
  );

  const completePendingFavorites = useCallback(() => {
    const favoritesToAdd = pendingFavorites.length > 0 ? pendingFavorites : readPendingFavorites();
    if (favoritesToAdd.length === 0) return;

    setPendingFavorites([]);
    clearPendingFavorites();
    setAuthModalOpen(false);
    favoritesToAdd.forEach((pendingFavorite) => {
      const override = updateFavoriteOverride(pendingFavorite.alias, true);
      submitFavoriteIntent(
        pendingFavorite.alias,
        true,
        favoritedAliasesRef.current.has(pendingFavorite.alias),
        override.updatedAt,
        undefined,
      );
    });
  }, [pendingFavorites, submitFavoriteIntent, updateFavoriteOverride]);

  useEffect(() => {
    const handleFavoriteOverrideEvent = (event: StorageEvent) => {
      if (event.key !== FAVORITE_OVERRIDE_EVENT_STORAGE_KEY) return;
      const overrideEvent = readFavoriteOverrideEvent(event.newValue);
      if (!overrideEvent) return;

      const { alias, ...override } = overrideEvent;
      if (!applyFavoriteOverride(alias, override, false) || !loggedIn) return;
      submitFavoriteIntent(
        alias,
        override.saved,
        favoritedAliasesRef.current.has(alias),
        override.updatedAt,
        undefined,
      );
    };

    window.addEventListener('storage', handleFavoriteOverrideEvent);
    return () => window.removeEventListener('storage', handleFavoriteOverrideEvent);
  }, [applyFavoriteOverride, loggedIn, submitFavoriteIntent]);

  // Reconcile only against fresh data: while a refetch is in flight the cached
  // list predates the last mutation, and resubmitting against it re-sends the
  // mutation and cancels the very refetch that would prove it already applied.
  useEffect(() => {
    if (!loggedIn || favoriteListingsData === undefined || favoriteListingsFetching) return;

    Object.entries(favoriteOverrides).forEach(([alias, override]) => {
      if (favoriteControllersRef.current.has(alias)) return;
      const serverSaved = favoritedAliases.has(alias);
      if (serverSaved === override.saved) return;
      if (resubmittedFavoriteRevisionsRef.current.get(alias) === override.updatedAt) return;
      resubmittedFavoriteRevisionsRef.current.set(alias, override.updatedAt);
      submitFavoriteIntent(alias, override.saved, serverSaved, override.updatedAt, undefined);
    });
  }, [
    favoriteListingsData,
    favoriteListingsFetching,
    favoriteOverrides,
    favoritedAliases,
    loggedIn,
    submitFavoriteIntent,
  ]);

  useEffect(() => {
    if (pendingFavorites.length === 0) return;

    setAuthRedirectPath(
      withPendingFavoriteRedirectParams(
        getCurrentRelativeUrl(),
        pendingFavorites.map((pendingFavorite) => pendingFavorite.alias),
      ),
    );

    if (currentUser?.loggedIn) {
      completePendingFavorites();
      return;
    }

    if (currentUser === null) {
      setAuthModalOpen(true);
    }
  }, [completePendingFavorites, currentUser, pendingFavorites]);

  const isListingHidden = useCallback(
    (listing: ListingLike | null | undefined) => {
      if (!currentUser?.loggedIn) return false;
      const alias = getListingActionAlias(listing);
      if (!alias) return false;

      const override = hiddenOverrides[alias];
      if (override !== undefined) return override;

      return hiddenAliases.has(alias) || listingHasHiddenUserItem(listing);
    },
    [currentUser?.loggedIn, hiddenAliases, hiddenOverrides],
  );

  const updateHiddenOverride = useCallback((alias: string, hidden: boolean) => {
    setHiddenOverrides((current) => ({ ...current, [alias]: hidden }));
  }, []);

  const showHideError = useCallback(() => {
    enqueueToast(
      <Toast tone="critical" role="status">
        {tHdp('hideError')}
      </Toast>,
    );
  }, [enqueueToast, tHdp]);

  const addHidden = useCallback(
    async (alias: string) => {
      updateHiddenOverride(alias, true);

      try {
        const postData = { interaction: HpModifiableUserInteraction.HIDE, listingAlias: alias };
        await addUserInteraction({ postData });
        enqueueToast(
          <Toast tone="success" role="status">
            {tHdp('listingHidden')}
          </Toast>,
        );
      } catch {
        updateHiddenOverride(alias, false);
        showHideError();
      }
    },
    [addUserInteraction, enqueueToast, showHideError, tHdp, updateHiddenOverride],
  );

  const removeHidden = useCallback(
    async (alias: string) => {
      updateHiddenOverride(alias, false);

      try {
        const postData = { interaction: HpModifiableUserInteraction.HIDE, listingAlias: alias };
        await removeUserInteraction({ postData });
      } catch {
        updateHiddenOverride(alias, true);
        showHideError();
      }
    },
    [removeUserInteraction, showHideError, updateHiddenOverride],
  );

  const completePendingHide = useCallback(() => {
    if (!pendingHide || completedHideRef.current === pendingHide) return;

    const hideToAdd = pendingHide;
    completedHideRef.current = hideToAdd;
    setPendingHide(null);
    setHideAuthModalOpen(false);
    analytics.emitTrackHideHomeFromHdpEvent(hideToAdd.analyticsContext);
    void addHidden(hideToAdd.alias);
  }, [addHidden, pendingHide]);

  useEffect(() => {
    if (!pendingHide) return;

    if (currentUser?.loggedIn) {
      completePendingHide();
      return;
    }

    if (currentUser === null) {
      setHideAuthModalOpen(true);
    }
  }, [completePendingHide, currentUser, pendingHide]);

  const toggleHideListing = useCallback(
    (
      listing: ListingLike | null | undefined,
      analyticsContext: ListingActionAnalyticsContext = DEFAULT_LISTING_ACTION_ANALYTICS,
    ) => {
      const alias = getListingActionAlias(listing);
      if (!listing || !alias) return;
      const clickstreamListing = getListingActionClickstreamListing(listing);
      const analyticsContextWithListing = { ...analyticsContext, listing: clickstreamListing };

      if (isListingHidden(listing)) {
        analytics.emitTrackUnhideHomeFromHdpEvent(analyticsContextWithListing);
        void removeHidden(alias);
        return;
      }

      if (!currentUser?.loggedIn) {
        setPendingHide({ alias, analyticsContext: analyticsContextWithListing });
        setHideAuthRedirectPath(getCurrentRelativeUrl());
        if (currentUser !== undefined) {
          setHideAuthModalOpen(true);
        }
        return;
      }

      analytics.emitTrackHideHomeFromHdpEvent(analyticsContextWithListing);
      void addHidden(alias);
    },
    [addHidden, currentUser, isListingHidden, removeHidden],
  );

  const handleHideAuthModalOpenChange = useCallback((open: boolean) => {
    setHideAuthModalOpen(open);
    if (open) return;
    setPendingHide(null);
  }, []);

  const openReport = useCallback(
    (
      listing: ListingActionListing | null | undefined,
      analyticsContext: ListingActionAnalyticsContext = DEFAULT_LISTING_ACTION_ANALYTICS,
    ) => {
      if (!listing) return;
      analytics.emitTrackReportHomeFromHdpEvent({
        ...analyticsContext,
        listing: getListingActionClickstreamListing(listing),
      });
      setActiveReport({ listing: listing as PropertyDetailListing | ExtendedListing });
    },
    [],
  );

  // Yields for INP: paints the tap feedback before mounting the contact modal.
  const openContact = useCallback(
    async (listing: ListingActionListing | null | undefined, options?: ListingActionOpenOptions) => {
      await interactionResponse();
      if (currentUser === undefined) return;

      const { title, initialMessage, ...analyticsOptions } = options ?? {};
      const clickstreamContext = {
        triggerLocation: analyticsOptions.triggerLocation ?? DEFAULT_LISTING_ACTION_ANALYTICS.triggerLocation,
        triggerObject: analyticsOptions.triggerObject ?? DEFAULT_LISTING_ACTION_ANALYTICS.triggerObject,
      };
      const clickstreamListing = getListingActionClickstreamListing(listing);

      analytics.emitTrackContactClickEvent({
        ...clickstreamContext,
        listing: clickstreamListing,
        phase: 'open',
      });

      setActiveContact({
        listing: listing as PropertyDetailListing | ExtendedListing | null | undefined,
        title: title ?? 'Contact property',
        clickstreamContext,
        initialMessage,
      });
    },
    [currentUser],
  );

  const openShare = useCallback(
    (
      listing: ListingActionListing | null | undefined,
      context: ShareClickstreamContext = SHARE_CLICKSTREAM_CONTEXTS.hdp,
    ) => {
      if (!listing) return;
      const clickstreamListing = getListingActionClickstreamListing(listing);

      analytics.emitTrackShareClickEvent({
        listing: clickstreamListing,
        ...context,
      });
      if (
        tryNativeShare(listing, () => {
          emitTrackHdpNativeShareEvent({
            listing: clickstreamListing,
            triggerLocation: context.triggerLocation,
          });
        })
      )
        return;

      setActiveShare({
        listing: listing as PropertyDetailListing | ExtendedListing,
        triggerLocation: context.triggerLocation,
      });
    },
    [],
  );

  const isListingSaved = useCallback(
    (listing: ListingLike | null | undefined) => {
      if (!currentUser?.loggedIn) return false;
      const alias = getListingActionAlias(listing);
      if (!alias) return false;

      const override = favoriteOverrides[alias];
      if (override !== undefined) return override.saved;

      return favoritedAliases.has(alias) || listingHasFavoriteUserItem(listing);
    },
    [currentUser?.loggedIn, favoriteOverrides, favoritedAliases],
  );

  const isListingSavePending = useCallback(
    (listing: ListingLike | null | undefined) => {
      const alias = getListingActionAlias(listing);
      return !!alias && pendingFavorites.some((pendingFavorite) => pendingFavorite.alias === alias);
    },
    [pendingFavorites],
  );

  const trackFavoriteIntent = useCallback(
    async (
      interaction: ReturnType<typeof interactionResponse>,
      saved: boolean,
      listing: ListingLike,
      analyticsContext: ListingActionAnalyticsContext,
    ) => {
      try {
        await interaction;
      } catch {
        return;
      }

      const analyticsContextWithListing = {
        ...analyticsContext,
        listing: getListingActionClickstreamListing(listing),
      };

      if (saved) {
        analytics.emitTrackSaveHomeClickEvent(analyticsContextWithListing);
      } else {
        analytics.emitTrackUnSaveHomeClickEvent(analyticsContextWithListing);
      }
    },
    [],
  );

  // Keep only the optimistic state update in the tap; defer auth, analytics, and API work,
  // which previously contributed ~255ms processing at 4x CPU.
  const toggleSaveListing = useCallback(
    async (
      listing: ListingLike | null | undefined,
      analyticsContext: ListingActionAnalyticsContext = DEFAULT_LISTING_ACTION_ANALYTICS,
    ) => {
      const alias = getListingActionAlias(listing);
      if (!listing || !alias) return false;
      const listingSaved = isListingSaved(listing);

      if (currentUser?.loggedIn) {
        const saved = !listingSaved;
        let interaction: ReturnType<typeof interactionResponse>;
        try {
          interaction = interactionResponse();
        } catch {
          return false;
        }

        const override = updateFavoriteOverride(alias, saved);
        submitFavoriteIntent(
          alias,
          saved,
          favoritedAliases.has(alias) || listingHasFavoriteUserItem(listing),
          override.updatedAt,
          interaction,
        );
        void trackFavoriteIntent(interaction, saved, listing, analyticsContext);
        try {
          await interaction;
          return true;
        } catch {
          return false;
        }
      }

      try {
        await interactionResponse();
      } catch {
        return false;
      }

      const clickstreamListing = getListingActionClickstreamListing(listing);
      const analyticsContextWithListing = { ...analyticsContext, listing: clickstreamListing };

      analytics.emitTrackSaveHomeClickEvent(analyticsContextWithListing);

      const nextPendingFavorite = {
        alias,
        analyticsContext: analyticsContextWithListing,
        requestedAt: Date.now(),
      };

      setPendingFavorites((currentPendingFavorites) => {
        const nextPendingFavorites = mergePendingFavorites(currentPendingFavorites, [nextPendingFavorite]);
        writePendingFavorites(nextPendingFavorites);
        return nextPendingFavorites;
      });
      return true;
    },
    [
      currentUser?.loggedIn,
      favoritedAliases,
      isListingSaved,
      submitFavoriteIntent,
      trackFavoriteIntent,
      updateFavoriteOverride,
    ],
  );

  const handleAuthModalOpenChange = useCallback((open: boolean) => {
    setAuthModalOpen(open);
    if (open) return;

    setPendingFavorites([]);
    clearPendingFavorites();
  }, []);

  const handleContactSubmitSuccess = useCallback((context: ContactSubmissionContext) => {
    setActiveContact(null);
    setPostContactContext(context);
  }, []);

  const value = useMemo(
    () => ({
      openContact,
      openShare,
      openReport,
      isListingSaved,
      isListingSavePending,
      isListingHidden,
      reconcileFavoriteOverride,
      toggleSaveListing,
      toggleHideListing,
    }),
    [
      openContact,
      openShare,
      openReport,
      isListingSaved,
      isListingSavePending,
      isListingHidden,
      reconcileFavoriteOverride,
      toggleSaveListing,
      toggleHideListing,
    ],
  );

  return (
    <ListingActionContext.Provider value={value}>
      {children}
      <Suspense fallback={null}>
        {activeContact && (
          <ContactFormOverlay
            open
            onOpenChange={(open) => !open && setActiveContact(null)}
            title={activeContact.title}
            listing={activeContact.listing}
            mode="contact"
            clickstreamContext={activeContact.clickstreamContext}
            onSubmitSuccess={handleContactSubmitSuccess}
            initialMessage={activeContact.initialMessage}
          />
        )}
        {postContactContext && (
          <PostContactFlowModal context={postContactContext} onDone={() => setPostContactContext(null)} />
        )}
        {activeShare && (
          <ShareFormOverlay
            open
            onOpenChange={(open) => !open && setActiveShare(null)}
            listing={activeShare.listing}
            triggerLocation={activeShare.triggerLocation}
          />
        )}
        {activeReport && (
          <ReportListingOverlay
            open
            onOpenChange={(open) => !open && setActiveReport(null)}
            listing={activeReport.listing}
          />
        )}
        <ListingActionSignInModal
          open={authModalOpen}
          onOpenChange={handleAuthModalOpenChange}
          onAuthenticated={completePendingFavorites}
          postLoginRedirect={authRedirectPath}
        />
        <ListingActionSignInModal
          open={hideAuthModalOpen}
          onOpenChange={handleHideAuthModalOpenChange}
          onAuthenticated={completePendingHide}
          postLoginRedirect={hideAuthRedirectPath}
          title={tAuth('hidePromptTitle')}
          intro={tAuth('hidePromptIntro')}
          testId="hide-sign-in-modal"
        />
      </Suspense>
    </ListingActionContext.Provider>
  );
}

export function useListingActionContext(): ListingActionContextValue {
  const context = useContext(ListingActionContext);

  if (!context) {
    throw new Error('useListingActionContext must be used within a ListingActionProvider');
  }

  return context;
}

export function useOptionalListingActionContext(): ListingActionContextValue | null {
  return useContext(ListingActionContext);
}

export type {
  ListingActionAnalyticsContext,
  ListingActionContextValue,
  ListingActionListing,
  ListingActionOpenOptions,
};
